import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { EmailDispatchLog, EmailDispatchLogDetail, EmailDispatchLogFilter, EmailDispatchPagination, EmailTrackingHttpService } from '@tsin-core/services/http/email-tracking.service';
import { catchError, tap } from 'rxjs';
import { ClearEmailDispatchLogs, ClearSelectedEmailDispatchLog, LoadEmailDispatchLog, LoadEmailDispatchLogs, SetEmailDispatchLogFilter } from './email-tracking.actions';

/** Default page size — matches the server default of 50.
 *  Taille de page par défaut — identique au défaut du serveur. */
export const EMAIL_DISPATCH_DEFAULT_LIMIT = 50;

/** Placeholder envelope used before the first response lands, so the view never
 *  has to null-check pagination.
 *  Enveloppe de remplacement utilisée avant la première réponse. */
const EMPTY_PAGINATION: EmailDispatchPagination = {
  page: 1,
  limit: EMAIL_DISPATCH_DEFAULT_LIMIT,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
  windowLimit: 0,
  totalMatching: 0,
};

const DEFAULT_FILTER: EmailDispatchLogFilter = { page: 1, limit: EMAIL_DISPATCH_DEFAULT_LIMIT };

export class EmailTrackingStateModel {
  /** Current page of the dispatch report / Page courante du rapport d'envois */
  logs: EmailDispatchLog[] = [];
  /** Server pagination envelope for the current page / Enveloppe de pagination */
  pagination: EmailDispatchPagination = EMPTY_PAGINATION;
  /** Filter the current page was fetched with / Filtre de la page courante */
  filter: EmailDispatchLogFilter = DEFAULT_FILTER;
  /** Dispatch opened in the detail view, with recipients / Envoi ouvert en détail */
  selectedDispatch: EmailDispatchLogDetail | null = null;
  /** Loading state for the report / État de chargement du rapport */
  loading: LoadingState = LoadingState.notLoading;
  /** Whether the detail fetch is in flight / Chargement du détail en cours */
  detailLoading: boolean = false;
  /** Error from the last report fetch / Erreur du dernier chargement du rapport */
  error: string | null = null;
  /** Error from the last detail fetch / Erreur du dernier chargement de détail */
  detailError: string | null = null;
}

const defaults: EmailTrackingStateModel = {
  logs: [],
  pagination: EMPTY_PAGINATION,
  filter: DEFAULT_FILTER,
  selectedDispatch: null,
  loading: LoadingState.notLoading,
  detailLoading: false,
  error: null,
  detailError: null,
};

@State<EmailTrackingStateModel>({ name: 'emailTrackingState', defaults })
@Injectable()
export class EmailTrackingState {
  private readonly svc = inject(EmailTrackingHttpService);

  // ─── Selectors ───────────────────────────────────────────────────────────

  @Selector() static logs(s: EmailTrackingStateModel) {
    return s.logs;
  }

  @Selector() static pagination(s: EmailTrackingStateModel) {
    return s.pagination;
  }

  @Selector() static filter(s: EmailTrackingStateModel) {
    return s.filter;
  }

  @Selector() static selectedDispatch(s: EmailTrackingStateModel) {
    return s.selectedDispatch;
  }

  @Selector() static loading(s: EmailTrackingStateModel) {
    return s.loading;
  }

  @Selector() static detailLoading(s: EmailTrackingStateModel) {
    return s.detailLoading;
  }

  @Selector() static error(s: EmailTrackingStateModel) {
    return s.error;
  }

  @Selector() static detailError(s: EmailTrackingStateModel) {
    return s.detailError;
  }

  /** True when the filter matches more rows than the server will page through,
   *  i.e. the report is truncated by the window cap.
   *  Vrai lorsque le rapport est tronqué par le plafond de fenêtre. */
  @Selector() static isWindowCapped(s: EmailTrackingStateModel) {
    return s.pagination.totalMatching > s.pagination.total;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  @Action(LoadEmailDispatchLogs)
  loadLogs({ patchState }: StateContext<EmailTrackingStateModel>, { filter }: LoadEmailDispatchLogs) {
    const applied: EmailDispatchLogFilter = { ...DEFAULT_FILTER, ...filter };
    patchState({ loading: LoadingState.loadingList, error: null, filter: applied });
    return this.svc.getDispatchLogs(applied).pipe(
      tap((res) =>
        patchState({
          logs: res?.data ?? [],
          pagination: res?.pagination ?? EMPTY_PAGINATION,
          loading: LoadingState.notLoading,
        }),
      ),
      catchError((e) => {
        patchState({ loading: LoadingState.notLoading, error: e?.error?.message ?? e?.message ?? 'Load failed / Chargement échoué' });
        throw e;
      }),
    );
  }

  @Action(SetEmailDispatchLogFilter)
  setFilter({ patchState }: StateContext<EmailTrackingStateModel>, { filter }: SetEmailDispatchLogFilter) {
    patchState({ filter: { ...DEFAULT_FILTER, ...filter } });
  }

  @Action(LoadEmailDispatchLog)
  loadLog({ patchState }: StateContext<EmailTrackingStateModel>, { id }: LoadEmailDispatchLog) {
    patchState({ detailLoading: true, detailError: null, selectedDispatch: null });
    return this.svc.getDispatchLog(id).pipe(
      tap((selectedDispatch) => patchState({ selectedDispatch, detailLoading: false })),
      catchError((e) => {
        patchState({ detailLoading: false, detailError: e?.status === 404 ? 'NOT_FOUND' : (e?.error?.message ?? e?.message ?? 'Load failed / Chargement échoué') });
        throw e;
      }),
    );
  }

  @Action(ClearSelectedEmailDispatchLog)
  clearSelected({ patchState }: StateContext<EmailTrackingStateModel>) {
    patchState({ selectedDispatch: null, detailLoading: false, detailError: null });
  }

  @Action(ClearEmailDispatchLogs)
  clear({ setState }: StateContext<EmailTrackingStateModel>) {
    setState(defaults);
  }
}
